import moment from 'moment'
import { create } from 'zustand'
import { createJSONStorage, devtools, persist } from 'zustand/middleware'
import http from '@lib/http-client'
import { parseUtcDate } from '@utils/date'
import {
  INeedsAttentionItem,
  INextMember,
  NeedsAttentionParams,
  NeedsAttentionResponse,
  NeedsAttentionStore
} from './types'

const NEEDS_ATTENTION_STORAGE_KEY = 'needs-attention-storage'

// a member opened while the previous one's lookup is on its way makes that
// lookup stale
let nextMemberRequest = 0

// enough of the queue to find where the open member sits in it
const NEXT_MEMBER_QUEUE_SIZE = 50

const today = () => moment().format('YYYY-MM-DD')

/* -----------------------------------------------------------------------------
 * pickNextMember
 *
 * The queue is the professional's members who need attention, in the Reports
 * order. The next member is the first one after the open member that was not
 * opened today, then the first one not opened anywhere in the queue (a member
 * who wrote in moves to the top). Once every other member was opened, the
 * first of those skipped comes back. A member who wrote in after being opened
 * counts as not opened again.
 * -------------------------------------------------------------------------- */

const pickNextMember = (
  queue: INeedsAttentionItem[],
  total: number,
  isPartial: boolean,
  currentUserId: number,
  openedMembers: Record<string, string>
): INextMember => {
  const isOpened = (item: INeedsAttentionItem) => {
    const openedAt = parseUtcDate(openedMembers[item.user_id])
    const lastActivity = parseUtcDate(item.last_visit_date)
    return !!openedAt && !!lastActivity && !lastActivity.isAfter(openedAt)
  }
  const others = queue.filter(item => item.user_id !== currentUserId)
  const position = queue.findIndex(item => item.user_id === currentUserId)
  const next =
    queue.slice(position + 1).find(item => !isOpened(item)) ??
    others.find(item => !isOpened(item))
  const base = { forUserId: currentUserId, isPartial, total }

  if (next) {
    return { ...base, isSkipped: false, skippedCount: 0, userId: next.user_id }
  }
  return {
    ...base,
    isSkipped: others.length > 0,
    skippedCount: others.length,
    userId: others[0]?.user_id ?? null
  }
}

export const useNeedsAttentionStore = create<NeedsAttentionStore>()(
  devtools(
    persist(
      (set, get) => ({
        nextMember: null,
        openedMembers: {},
        openedOn: null,
        nextMemberButtonY: 0.6,

        getMemberNeedsAttention: async ({
          page = 0,
          pageSize,
          professional_ids = []
        }: NeedsAttentionParams = {}) => {
          try {
            const params = new URLSearchParams()
            params.append('page', String(page))
            if (pageSize) {
              params.append('page_size', String(pageSize))
            }
            if (professional_ids.length > 0) {
              params.append('professional_id', professional_ids.join(','))
            }

            const response = await http.get(
              `/dashboard/member_needs_attention?${params.toString()}`
            )

            if (!response || !Array.isArray(response.results)) {
              return null
            }

            return response as NeedsAttentionResponse
          } catch (error) {
            console.error('Error fetching member needs attention:', error)
            return null
          }
        },

        loadNextMember: async (
          currentUserId: number,
          professionalId: number,
          { reveal = false } = {}
        ) => {
          const request = ++nextMemberRequest
          const response = await get().getMemberNeedsAttention({
            page: 0,
            pageSize: NEXT_MEMBER_QUEUE_SIZE,
            professional_ids: [professionalId]
          })
          if (request !== nextMemberRequest) {
            return
          }
          // a failed lookup offers nobody rather than a stale member
          if (!response) {
            set({ nextMember: null })
            return
          }

          const { openedMembers, openedOn } = get()
          // a queue longer than the page loaded is counted by what Next can
          // reach, so the bar does not promise members it never offers
          const nextMember = pickNextMember(
            response.results,
            response.has_next_page
              ? response.results.length
              : response.total_events,
            response.has_next_page,
            currentUserId,
            openedOn === today() ? openedMembers : {}
          )
          set({
            nextMember: reveal
              ? { ...nextMember, revealedAt: Date.now() }
              : nextMember
          })
        },

        markMemberOpened: (userId: number) => {
          const { openedMembers, openedOn } = get()
          set({
            openedMembers: {
              ...(openedOn === today() ? openedMembers : {}),
              [userId]: new Date().toISOString()
            },
            openedOn: today()
          })
        },

        setNextMemberButtonY: (y: number) => {
          set({ nextMemberButtonY: Math.min(Math.max(y, 0), 1) })
        }
      }),
      {
        name: NEEDS_ATTENTION_STORAGE_KEY,
        // kept across sessions, so the button stays where it was dragged and
        // the members opened today count in every tab
        storage: createJSONStorage(() => localStorage),
        partialize: state => ({
          nextMemberButtonY: state.nextMemberButtonY,
          openedMembers: state.openedMembers,
          openedOn: state.openedOn
        })
      }
    )
  )
)

// a member opened or the button moved in another tab
window.addEventListener('storage', event => {
  if (event.key === NEEDS_ATTENTION_STORAGE_KEY) {
    useNeedsAttentionStore.persist.rehydrate()
  }
})
